import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import LockOutlined from '@mui/icons-material/LockOutlined';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import { Alert, Box, Button, Card, Skeleton, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { ApiError } from '../api/client';
import { errorMessage } from '../lib/notify';

export function PageHeader({
  title,
  subtitle,
  eyebrow,
  actions,
}: {
  title: string;
  subtitle?: ReactNode;
  /** Small line above the title (breadcrumb, section). */
  eyebrow?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={2}
      sx={{ alignItems: { sm: 'flex-end' }, justifyContent: 'space-between', mb: 3.5 }}
    >
      <Box sx={{ minWidth: 0 }}>
        {eyebrow && (
          <Typography
            variant="overline"
            color="primary"
            component="div"
            sx={{ mb: 0.5, lineHeight: 1.6 }}
          >
            {eyebrow}
          </Typography>
        )}
        <Typography variant="h3" component="h1">
          {title}
        </Typography>
        {subtitle && (
          <Typography color="text.secondary" sx={{ mt: 0.75 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {actions && (
        <Stack direction="row" spacing={1.25} sx={{ flexWrap: 'wrap', rowGap: 1.25 }}>
          {actions}
        </Stack>
      )}
    </Stack>
  );
}

/** Friendly empty state with an icon tile and one primary action. */
export function EmptyState({
  icon,
  title,
  body,
  action,
  dense,
}: {
  icon: ReactNode;
  title: string;
  body: ReactNode;
  action?: { label: string; onClick: () => void };
  /** Less padding, for empty panels inside a page. */
  dense?: boolean;
}) {
  return (
    <Box sx={{ textAlign: 'center', py: dense ? 4 : { xs: 6, sm: 10 }, px: 3 }}>
      <Box
        sx={(t) => {
          const p = t.vars?.palette.primary.main ?? t.palette.primary.main;
          const size = dense ? 52 : 72;
          return {
            width: size,
            height: size,
            mx: 'auto',
            mb: dense ? 1.5 : 2.5,
            borderRadius: dense ? '16px' : '20px',
            display: 'grid',
            placeItems: 'center',
            color: 'primary.main',
            background: `linear-gradient(135deg, color-mix(in srgb, ${p} 16%, transparent), color-mix(in srgb, ${p} 4%, transparent))`,
            border: `1px solid color-mix(in srgb, ${p} 18%, transparent)`,
            '& svg': { fontSize: dense ? 26 : 34 },
          };
        }}
      >
        {icon}
      </Box>
      <Typography variant={dense ? 'h6' : 'h5'} component="p" sx={{ mb: 0.75 }}>
        {title}
      </Typography>
      <Typography
        color="text.secondary"
        variant={dense ? 'body2' : 'body1'}
        sx={{ maxWidth: 420, mx: 'auto' }}
      >
        {body}
      </Typography>
      {action && (
        <Button variant="contained" sx={{ mt: 3 }} onClick={action.onClick}>
          {action.label}
        </Button>
      )}
    </Box>
  );
}

/** Shown in place of a screen the admin's role cannot use. */
export function NoAccess({ what = 'this page' }: { what?: string }) {
  return (
    <Card>
      <EmptyState
        icon={<LockOutlined />}
        title="Not available for your role"
        body={`Your platform role doesn’t include access to ${what}. A super admin can change your role.`}
      />
    </Card>
  );
}

function requestIdOf(error: unknown): string | undefined {
  return error instanceof ApiError ? error.requestId : undefined;
}

/** Inline error for a failed query: message, request id and a retry button. */
export function QueryError({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const requestId = requestIdOf(error);
  return (
    <Alert
      severity="error"
      icon={<ErrorOutlineRounded />}
      action={
        onRetry && (
          <Button color="inherit" size="small" onClick={onRetry}>
            Retry
          </Button>
        )
      }
      sx={{ mb: 2.5 }}
    >
      {errorMessage(error)}
      {requestId && (
        <Typography variant="caption" component="div" sx={{ opacity: 0.8 }}>
          Request ID: <span className="tabular">{requestId}</span>
        </Typography>
      )}
    </Alert>
  );
}

/**
 * A whole screen (or panel) that could not load. 404s get a "not available on this server yet"
 * message: the console can run against an API that lacks a newer endpoint.
 */
export function ErrorState({
  error,
  onRetry,
  title,
  dense,
}: {
  error: unknown;
  onRetry?: () => void;
  title?: string;
  dense?: boolean;
}) {
  const missing = error instanceof ApiError && error.status === 404;
  const requestId = requestIdOf(error);
  return (
    <Box sx={{ textAlign: 'center', py: dense ? 4 : { xs: 6, sm: 9 }, px: 3 }} role="alert">
      <Box
        sx={(t) => {
          const c = t.vars?.palette.error.main ?? t.palette.error.main;
          return {
            width: dense ? 48 : 64,
            height: dense ? 48 : 64,
            mx: 'auto',
            mb: 2,
            borderRadius: '18px',
            display: 'grid',
            placeItems: 'center',
            color: 'error.main',
            bgcolor: `color-mix(in srgb, ${c} 10%, transparent)`,
            border: `1px solid color-mix(in srgb, ${c} 18%, transparent)`,
            '& svg': { fontSize: dense ? 24 : 30 },
          };
        }}
      >
        <ErrorOutlineRounded />
      </Box>
      <Typography variant={dense ? 'h6' : 'h5'} component="p" sx={{ mb: 0.75 }}>
        {title ?? (missing ? 'Not available on this server yet' : 'Couldn’t load this')}
      </Typography>
      <Typography color="text.secondary" variant="body2" sx={{ maxWidth: 440, mx: 'auto' }}>
        {missing
          ? 'The API you are connected to does not offer this endpoint. It arrives with a newer backend release.'
          : errorMessage(error)}
      </Typography>
      {requestId && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
          Request ID: <span className="tabular">{requestId}</span>
        </Typography>
      )}
      {onRetry && (
        <Button
          variant="outlined"
          startIcon={<RefreshRounded />}
          onClick={onRetry}
          sx={{ mt: 2.5 }}
        >
          Try again
        </Button>
      )}
    </Box>
  );
}

/** Skeleton block for a loading card body. */
export function LoadingRows({ rows = 4, height = 44 }: { rows?: number; height?: number }) {
  return (
    <Stack spacing={1} aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} variant="rounded" height={height} />
      ))}
    </Stack>
  );
}
